import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  staffGetTables,
  staffGetMenu,
  staffAddTableItem,
  staffAddOrderItem,
  staffCreateTakeaway,
  staffSubmitOrder,
  staffIncLine,
  staffDecLine,
  staffGetOpenOrders,
  resolveMediaUrl,
} from '../lib/api';
import { toppingPrice } from '../lib/pizzaPricing';
import {
  Loader2,
  Send,
  Plus,
  Minus,
  ShoppingBag,
  LayoutGrid,
  UtensilsCrossed,
  MoreVertical,
} from 'lucide-react';

const POLL_MS = 8000;
const SIZES = [
  { key: 'mediana', label: 'Mediana' },
  { key: 'grande', label: 'Grande' },
  { key: 'familiar', label: 'Familiar' },
];

function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function itemPriceHint(item, sizeKey) {
  if (Array.isArray(item.sizes) && item.sizes.length) {
    const s = item.sizes.find((x) => x.key === sizeKey) || item.sizes[0];
    return Number(s?.price ?? item.price ?? 0);
  }
  return Number(item.price || 0);
}

function isPizzaItem(item) {
  if (!item) return false;
  if (item.item_kind === 'topping_extra') return false;
  if (item.is_pizza === true || item.item_kind === 'pizza') return true;
  if (item.is_pizza === false) return false;
  return Array.isArray(item.sizes) && item.sizes.length > 0;
}

function isExtrasCategory(cat) {
  const blob = `${cat?.name || ''} ${cat?.slug || ''}`.toLowerCase();
  return /ingrediente|extra|topping/.test(blob);
}

function lineExtrasLabel(ln) {
  return (ln.extra_toppings || []).map((t) => t.name).filter(Boolean);
}

export default function AdminComanda() {
  const [tables, setTables] = useState([]);
  const [takeaways, setTakeaways] = useState([]);
  const [menu, setMenu] = useState({ categories: [], topping_extras: [] });
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [selectedTakeawayId, setSelectedTakeawayId] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [pickingTables, setPickingTables] = useState(true);
  const [showTakeawayPrompt, setShowTakeawayPrompt] = useState(false);
  const [takeawayName, setTakeawayName] = useState('');
  const [categoryId, setCategoryId] = useState(null);
  const [pizzaDraft, setPizzaDraft] = useState(null); // { item, sizeKey, extraIds }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const toppingExtras = useMemo(() => menu.topping_extras || [], [menu]);

  const categories = useMemo(
    () =>
      (menu.categories || []).filter(
        (c) =>
          !isExtrasCategory(c) &&
          (c.items || []).some((it) => it.item_kind !== 'topping_extra')
      ),
    [menu]
  );

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryId) || categories[0] || null,
    [categories, categoryId]
  );

  const selectedTable = useMemo(
    () => tables.find((t) => t.id === selectedTableId) || null,
    [tables, selectedTableId]
  );

  const pizzaPreviewUnit = useMemo(() => {
    if (!pizzaDraft?.item || !pizzaDraft.sizeKey) return 0;
    const base = itemPriceHint(pizzaDraft.item, pizzaDraft.sizeKey);
    const extras = toppingExtras
      .filter((t) => (pizzaDraft.extraIds || []).includes(t.id))
      .reduce((s, t) => s + toppingPrice(t, pizzaDraft.sizeKey), 0);
    return base + extras;
  }, [pizzaDraft, toppingExtras]);

  const load = useCallback(async () => {
    try {
      const [t, m, open] = await Promise.all([
        staffGetTables(),
        staffGetMenu(),
        staffGetOpenOrders().catch(() => []),
      ]);
      setTables(Array.isArray(t) ? t : []);
      setMenu(m || { categories: [], topping_extras: [] });
      const tw = (Array.isArray(open) ? open : []).filter(
        (o) =>
          o.channel === 'waiter' &&
          (o.service_type === 'takeaway' || (!o.table_id && o.delivery_type === 'pickup')) &&
          !['closed', 'cancelled', 'delivered'].includes(String(o.status || ''))
      );
      setTakeaways(tw);

      if (selectedTableId) {
        const row = (t || []).find((x) => x.id === selectedTableId);
        if (row?.order) setActiveOrder(row.order);
      } else if (selectedTakeawayId) {
        const o = tw.find((x) => x.id === selectedTakeawayId);
        if (o) setActiveOrder(o);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar comanda');
    } finally {
      setLoading(false);
    }
  }, [selectedTableId, selectedTakeawayId]);

  useEffect(() => {
    load();
    const t = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!categories.some((c) => c.id === categoryId)) {
      setCategoryId(categories[0]?.id || null);
    }
  }, [categories, categoryId]);

  const openTable = (table) => {
    setSelectedTableId(table.id);
    setSelectedTakeawayId(null);
    setActiveOrder(table.order || null);
    setPickingTables(false);
  };

  const openTakeaway = (order) => {
    setSelectedTakeawayId(order.id);
    setSelectedTableId(null);
    setActiveOrder(order);
    setPickingTables(false);
  };

  const createTakeaway = async () => {
    const name = takeawayName.trim();
    if (!name) {
      toast.error('Indica el nombre del cliente');
      return;
    }
    setBusy(true);
    try {
      const order = await staffCreateTakeaway(name);
      setShowTakeawayPrompt(false);
      setTakeawayName('');
      setSelectedTableId(null);
      setSelectedTakeawayId(order.id);
      setActiveOrder(order);
      setPickingTables(false);
      toast.success(`Para llevar: ${name}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const addItem = async (item, opts = {}) => {
    if (!selectedTableId && !activeOrder?.id) {
      toast.error('Elige una mesa o para llevar');
      return;
    }
    const pizza = isPizzaItem(item);
    if (pizza && !opts.sizeKey) {
      setPizzaDraft({ item, sizeKey: null, extraIds: [] });
      return;
    }
    const sk = opts.sizeKey || 'mediana';
    const extraIds = opts.extraIds || [];
    setBusy(true);
    try {
      const body = {
        menu_item_id: item.id,
        qty: 1,
        size_key: pizza ? sk : 'mediana',
        extra_topping_ids: pizza ? extraIds : [],
      };
      let order;
      if (selectedTableId) {
        order = await staffAddTableItem(selectedTableId, body);
      } else {
        order = await staffAddOrderItem(activeOrder.id, body);
      }
      setActiveOrder(order);
      setPizzaDraft(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo agregar');
    } finally {
      setBusy(false);
    }
  };

  const confirmPizzaDraft = async () => {
    if (!pizzaDraft?.item || !pizzaDraft.sizeKey) {
      toast.error('Elige una talla');
      return;
    }
    await addItem(pizzaDraft.item, {
      sizeKey: pizzaDraft.sizeKey,
      extraIds: pizzaDraft.extraIds || [],
    });
  };

  const submit = async () => {
    if (!activeOrder?.id) return;
    setBusy(true);
    try {
      const order = await staffSubmitOrder(activeOrder.id);
      setActiveOrder(order);
      toast.success('Enviado a cocina');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setBusy(false);
    }
  };

  const lines = (activeOrder?.lines || []).filter((l) => l.line_status !== 'cancelled');
  const draftCount = lines.filter((l) => l.line_status === 'draft').length;

  const headerLabel = selectedTakeawayId
    ? activeOrder?.customer_name || 'Para llevar'
    : selectedTable
      ? `Mesa ${selectedTable.number} · ${selectedTable.capacity || 4} comensales`
      : '';

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-zinc-950 text-zinc-400 rounded-2xl">
        <Loader2 className="animate-spin mr-2" /> Cargando comanda…
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl bg-zinc-950 text-zinc-100 -mx-2 sm:mx-0 p-3 sm:p-4 min-h-[calc(100vh-7rem)] flex flex-col"
      data-testid="admin-comanda"
    >
      {pickingTables ? (
        <div className="flex-1 flex flex-col min-h-0">
          <h2 className="text-xl font-semibold mb-3 shrink-0">Mesas</h2>

          <button
            type="button"
            onClick={() => {
              setTakeawayName('');
              setShowTakeawayPrompt(true);
            }}
            className="mb-4 w-full rounded-xl border-2 border-dashed border-orange-400 bg-orange-950/25 p-4 text-center touch-manipulation min-h-[56px] flex items-center justify-center gap-2 font-semibold text-orange-100"
          >
            <ShoppingBag size={18} className="shrink-0" />
            <span>Para llevar</span>
          </button>

          {takeaways.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-2">
                Para llevar ({takeaways.length})
              </p>
              <div className="grid grid-cols-2 gap-2">
                {takeaways.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => openTakeaway(o)}
                    className="rounded-xl border-2 border-zinc-700 bg-zinc-900 p-3 flex flex-col items-center justify-center text-center min-h-[5.5rem] gap-1 touch-manipulation"
                  >
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                    <p className="font-bold text-sm line-clamp-2 px-1">
                      {o.customer_name || 'Sin nombre'}
                    </p>
                    <p className="text-xs text-orange-400 tabular-nums">{money(o.total)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-auto">
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {tables.map((table) => {
                const occupied = Boolean(table.order);
                return (
                  <button
                    key={table.id}
                    type="button"
                    onClick={() => openTable(table)}
                    className={`rounded-xl border-2 p-3 flex flex-col items-center justify-center text-center min-h-[5.5rem] gap-1.5 touch-manipulation ${
                      occupied
                        ? 'border-amber-600/70 bg-amber-950/30'
                        : 'border-zinc-700 bg-zinc-900'
                    }`}
                  >
                    <span
                      className={`h-2.5 w-2.5 rounded-full ${
                        occupied ? 'bg-amber-400' : 'bg-emerald-400'
                      }`}
                    />
                    <p className="font-bold text-sm">Mesa {table.number}</p>
                    {occupied ? (
                      <p className="text-xs text-orange-400 tabular-nums">
                        {money(table.order?.total)}
                      </p>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          {!tables.length && (
            <p className="text-sm text-zinc-500 mt-4">
              No hay mesas. Créalas en Config tienda → Mesas.
            </p>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 gap-3">
          <button
            type="button"
            onClick={() => setPickingTables(true)}
            className="shrink-0 w-full rounded-2xl border-2 border-orange-500 bg-orange-950/30 px-4 py-3 min-h-[52px] touch-manipulation flex items-center justify-center gap-3 hover:bg-orange-950/50 transition-colors"
          >
            <LayoutGrid size={20} className="shrink-0 text-orange-400" />
            <span className="flex-1 min-w-0 text-center sm:text-left">
              <span className="block text-base font-bold text-orange-50 truncate">{headerLabel}</span>
            </span>
            <span className="text-sm font-semibold text-orange-400 shrink-0">Cambiar mesa</span>
          </button>

          <div className="flex-1 min-h-0 grid lg:grid-cols-2 gap-3">
            {/* Menú */}
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/80 p-3 sm:p-4 flex flex-col min-h-0 overflow-hidden">
              <h2 className="text-xl font-semibold mb-3 shrink-0">Menú</h2>
              <div className="flex flex-wrap gap-2 mb-2 shrink-0">
                {categories.map((cat) => {
                  const active = selectedCategory?.id === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategoryId(cat.id)}
                      className={`rounded-full px-3.5 py-1.5 text-sm font-medium border touch-manipulation transition ${
                        active
                          ? 'bg-orange-950/60 border-orange-500 text-orange-100'
                          : 'bg-zinc-950 border-zinc-600 text-zinc-300'
                      }`}
                    >
                      {cat.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-zinc-500 mb-3 shrink-0">Toca o arrastra al pedido</p>

              <div className="flex-1 min-h-0 overflow-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(selectedCategory?.items || [])
                    .filter((item) => item.item_kind !== 'topping_extra')
                    .map((item) => {
                      const src = item.image_url
                        ? resolveMediaUrl(item.image_url)
                        : item.id
                          ? resolveMediaUrl(`/api/menu/items/${item.id}/image`)
                          : '';
                      const price = itemPriceHint(item, 'mediana');
                      return (
                        <button
                          key={item.id}
                          type="button"
                          disabled={busy}
                          onClick={() => addItem(item)}
                          className="relative rounded-xl border border-zinc-700 bg-zinc-950 p-3 min-h-[7.5rem] flex flex-col items-center justify-center text-center gap-1 touch-manipulation hover:border-orange-500/60 disabled:opacity-50"
                        >
                          <MoreVertical
                            size={14}
                            className="absolute top-2 right-2 text-zinc-600"
                          />
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-800 flex items-center justify-center shrink-0">
                            {src ? (
                              <img
                                src={src}
                                alt=""
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <UtensilsCrossed size={22} className="text-zinc-500" />
                            )}
                          </div>
                          <p className="font-medium text-sm leading-snug line-clamp-2 w-full px-1">
                            {item.name}
                          </p>
                          <p className="text-sm font-semibold text-orange-400 tabular-nums">
                            {money(price)}
                          </p>
                        </button>
                      );
                    })}
                </div>
                {!selectedCategory?.items?.length && (
                  <p className="text-sm text-zinc-500 py-8 text-center">Sin artículos</p>
                )}
              </div>
            </div>

            {/* Pedido */}
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/80 p-3 sm:p-4 flex flex-col min-h-0 overflow-hidden">
              <h2 className="text-lg font-semibold truncate mb-3 shrink-0">
                {selectedTakeawayId ? 'Para llevar' : `Pedido mesa ${selectedTable?.number ?? ''}`}
              </h2>

              <div className="flex-1 min-h-0 overflow-auto">
                {!lines.length ? (
                  <div className="h-full min-h-[12rem] flex items-center justify-center">
                    <p className="text-zinc-500 text-sm text-center px-4">
                      Toca o arrastra platillos al pedido
                    </p>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {lines.map((ln) => {
                      const extras = lineExtrasLabel(ln);
                      return (
                        <li
                          key={ln.line_id}
                          className={`rounded-xl border px-3 py-2 ${
                            ln.line_status === 'draft'
                              ? 'border-amber-600/50 bg-amber-950/20'
                              : 'border-sky-700/40 bg-sky-950/20'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-sm">
                                {ln.qty}× {ln.name}
                                {ln.size_label ? (
                                  <span className="text-zinc-400"> · {ln.size_label}</span>
                                ) : null}
                              </p>
                              {ln.half_specialty_name ? (
                                <p className="text-xs text-zinc-400">
                                  Mitad: {ln.half_specialty_name}
                                </p>
                              ) : null}
                              {extras.length ? (
                                <p className="text-xs text-amber-300/90 mt-0.5">
                                  Extra: {extras.join(', ')}
                                </p>
                              ) : null}
                              <p className="text-xs text-orange-400 tabular-nums mt-0.5">
                                {money(ln.line_total)}
                              </p>
                            </div>
                            {ln.line_status === 'draft' && activeOrder?.id ? (
                              <div className="flex gap-1 shrink-0">
                                <button
                                  type="button"
                                  disabled={busy}
                                  className="h-8 w-8 rounded-lg border border-zinc-600 flex items-center justify-center"
                                  onClick={async () => {
                                    setBusy(true);
                                    try {
                                      setActiveOrder(
                                        await staffDecLine(activeOrder.id, ln.line_id)
                                      );
                                      await load();
                                    } finally {
                                      setBusy(false);
                                    }
                                  }}
                                >
                                  <Minus size={14} />
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  className="h-8 w-8 rounded-lg border border-zinc-600 flex items-center justify-center"
                                  onClick={async () => {
                                    setBusy(true);
                                    try {
                                      setActiveOrder(
                                        await staffIncLine(activeOrder.id, ln.line_id)
                                      );
                                    } finally {
                                      setBusy(false);
                                    }
                                  }}
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="shrink-0 border-t border-zinc-700 mt-3 pt-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-orange-400 tabular-nums text-lg">
                    {money(activeOrder?.total)}
                  </span>
                </div>
                <button
                  type="button"
                  disabled={busy || !draftCount}
                  onClick={submit}
                  className="w-full min-h-[56px] inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-600 hover:bg-orange-500 disabled:opacity-40 px-4 py-3.5 text-base sm:text-lg font-bold touch-manipulation shadow-lg shadow-orange-950/40"
                >
                  <Send size={22} className="shrink-0" />
                  Enviar a cocina{draftCount ? ` (${draftCount})` : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt para llevar */}
      {showTakeawayPrompt && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-md rounded-2xl bg-zinc-900 border border-zinc-700 p-4 space-y-3">
            <h3 className="text-lg font-semibold">Para llevar</h3>
            <p className="text-sm text-zinc-400">Nombre del cliente</p>
            <input
              autoFocus
              className="w-full rounded-xl bg-zinc-950 border border-zinc-600 px-3 py-2.5 text-zinc-100 outline-none focus:border-orange-500"
              value={takeawayName}
              onChange={(e) => setTakeawayName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') createTakeaway();
              }}
              placeholder="Ej. Juan"
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="px-4 py-2 rounded-xl text-sm text-zinc-300"
                onClick={() => setShowTakeawayPrompt(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={busy}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-orange-600 hover:bg-orange-500 disabled:opacity-50"
                onClick={createTakeaway}
              >
                Abrir pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pizza: talla + ingredientes extra */}
      {pizzaDraft?.item && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl bg-zinc-900 border border-zinc-700 p-4 space-y-4">
            <h3 className="text-lg font-semibold">{pizzaDraft.item.name}</h3>

            <div>
              <p className="text-sm text-zinc-400 mb-2">1. Elige talla</p>
              <div className="grid gap-2">
                {SIZES.map((s) => {
                  const price = itemPriceHint(pizzaDraft.item, s.key);
                  const active = pizzaDraft.sizeKey === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        setPizzaDraft((d) => ({ ...d, sizeKey: s.key, extraIds: d.extraIds || [] }))
                      }
                      className={`flex items-center justify-between rounded-xl border px-4 py-3 touch-manipulation ${
                        active
                          ? 'border-orange-500 bg-orange-950/40'
                          : 'border-zinc-600 hover:border-orange-500/70'
                      }`}
                    >
                      <span className="font-medium">{s.label}</span>
                      <span className="text-orange-400 font-semibold">{money(price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {pizzaDraft.sizeKey && toppingExtras.length > 0 ? (
              <div>
                <p className="text-sm text-zinc-400 mb-2">2. Ingredientes extra (opcional)</p>
                <div className="max-h-48 overflow-y-auto space-y-1 rounded-xl border border-zinc-700 p-2">
                  {toppingExtras.map((t) => {
                    const checked = (pizzaDraft.extraIds || []).includes(t.id);
                    const price = toppingPrice(t, pizzaDraft.sizeKey);
                    return (
                      <label
                        key={t.id}
                        className="flex items-center gap-3 rounded-lg px-2 py-2.5 touch-manipulation hover:bg-zinc-800/80 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="h-5 w-5 accent-orange-500"
                          checked={checked}
                          onChange={() =>
                            setPizzaDraft((d) => {
                              const ids = d.extraIds || [];
                              return {
                                ...d,
                                extraIds: checked
                                  ? ids.filter((id) => id !== t.id)
                                  : [...ids, t.id],
                              };
                            })
                          }
                        />
                        <span className="flex-1 text-sm font-medium">{t.name}</span>
                        <span className="text-sm text-orange-400 tabular-nums">+{money(price)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {pizzaDraft.sizeKey ? (
              <p className="text-base font-bold text-orange-300">
                Unidad: {money(pizzaPreviewUnit)}
              </p>
            ) : null}

            <div className="flex flex-col gap-2 pt-1">
              <button
                type="button"
                disabled={busy || !pizzaDraft.sizeKey}
                onClick={confirmPizzaDraft}
                className="w-full min-h-[52px] rounded-2xl bg-orange-600 hover:bg-orange-500 disabled:opacity-40 font-bold text-base touch-manipulation"
              >
                Agregar al pedido
              </button>
              <button
                type="button"
                className="w-full py-2 text-sm text-zinc-400"
                onClick={() => setPizzaDraft(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
